import { isAndroid } from "@memorang/helpers/platform";
import { Appbar, Button, Surface } from "react-native-paper";
import { useAppConfig } from "../../../contexts/AppConfigContext";
import { useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import Box from "../../Box";
import TooltipWrapper from "../../TooltipWrapper";
import type { QuestionsConfig } from "../StudySessionBottomAppbar/NavButtons";
import StudySessionProgressbar from "../StudySessionProgressbar/StudySessionProgressbar";
import StudySessionAppbarLeft from "./StudySessionAppbarLeft";
import StudySessionAppbarRight from "./StudySessionAppbarRight";

type ToolbarActionType = "calculator" | "highlight" | "mark";

type Props = {
	userName: string;
	numericId: number;
	examName: string;
	timeRemaining: string;
	showPause?: boolean;
	handleClickRightCta: () => void;
	isTimerExpired?: boolean;
	inReviewMode?: boolean;
	blocks: { currentProgress?: number }[];
	currentBlockIndex: number;
	numTotalBlocks: number;
	partProgressLabel?: string;
	numCurrentSessionItems: number;
	currentItemIndex: number;
	skillName: string;
	handleClickMenuItem?: (type: "pause" | "help") => void;
	handleClickToolbarActions: (type: ToolbarActionType) => void;
	isMarked?: boolean;
	showToolbar?: boolean;
	showCalculator?: boolean;
	tooltips?: {
		calculator: string;
		highlight: string;
		mark: string;
		unmark: string;
	};
	questionsMenuConfig?: QuestionsConfig;
};

type MarkToolbarButtonProps = {
	isMarked?: boolean;
	onPress: () => void;
	tooltips?: Props["tooltips"];
};

const MarkToolbarButton = ({
	isMarked,
	onPress,
	tooltips,
}: MarkToolbarButtonProps) => {
	const theme = useAppTheme();
	const markActionIcon = isMarked ? "flag" : "flag-variant-outline";
	const markActionLabel = isMarked ? "Marked" : "Mark";
	const markActionColor = isMarked
		? theme.palette.error.main
		: theme.colors.onSurface;
	const markActionBackgroundColor = isMarked
		? theme.palette.highlight.main
		: undefined;
	const tooltipTitle = isMarked
		? (tooltips?.unmark ?? "")
		: (tooltips?.mark ?? "");

	return (
		<TooltipWrapper title={tooltipTitle}>
			<Button
				onPress={onPress}
				icon={markActionIcon}
				textColor={markActionColor}
				style={{
					backgroundColor: markActionBackgroundColor,
				}}
			>
				{markActionLabel}
			</Button>
		</TooltipWrapper>
	);
};

const SummativeSessionAppbar = ({
	userName,
	numericId,
	examName,
	timeRemaining,
	showPause,
	handleClickRightCta,
	isTimerExpired,
	inReviewMode,
	blocks,
	currentBlockIndex,
	numTotalBlocks,
	partProgressLabel,
	numCurrentSessionItems,
	currentItemIndex,
	skillName,
	handleClickMenuItem,
	handleClickToolbarActions,
	isMarked,
	showToolbar = true,
	showCalculator,
	tooltips,
	questionsMenuConfig,
}: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { allowMarking = true } = useAppConfig();

	if (isMobile) {
		return (
			<Box>
				<Appbar.Header
					mode="small"
					style={{
						justifyContent: "center",
						paddingTop: isAndroid ? 8 : 0,
						backgroundColor: theme.palette.info.dark,
						height: 80,
					}}
				>
					<Box position="relative" width="100%">
						<StudySessionProgressbar
							isSummativeTest
							showClose
							handleSelectClose={handleClickRightCta}
							showMenuIcon
							handleClickMenuItem={handleClickMenuItem}
							questionsMenuConfig={questionsMenuConfig}
							timeRemaining={timeRemaining}
							blocks={blocks}
							currentBlockIndex={currentBlockIndex}
							numTotalBlocks={numTotalBlocks}
							partProgressLabel={partProgressLabel}
							numCurrentSessionItems={numCurrentSessionItems}
							inReviewMode={inReviewMode}
							currentItemIndex={currentItemIndex}
						/>
					</Box>
				</Appbar.Header>
				{showToolbar && inReviewMode && allowMarking && (
					<Surface style={{ backgroundColor: theme.colors.surface }}>
						<Box justifyContent="flex-end" flexDirection="row" padding={16}>
							<MarkToolbarButton
								isMarked={isMarked}
								tooltips={tooltips}
								onPress={() => handleClickToolbarActions("mark")}
							/>
						</Box>
					</Surface>
				)}
			</Box>
		);
	}

	return (
		<Box>
			<Appbar.Header
				mode="medium"
				style={{ backgroundColor: theme.palette.info.dark }}
			>
				<Box
					justifyContent="space-between"
					flexDirection="row"
					width="100%"
					padding={16}
					alignItems="center"
				>
					<Box flex={1}>
						<StudySessionAppbarLeft
							userName={userName}
							numericId={numericId}
							title={examName}
						/>
					</Box>
					<Box flex={1} maxWidth={800}>
						<StudySessionProgressbar
							isSummativeTest
							timeRemaining={timeRemaining}
							blocks={blocks}
							currentBlockIndex={currentBlockIndex}
							numTotalBlocks={numTotalBlocks}
							partProgressLabel={partProgressLabel}
							numCurrentSessionItems={numCurrentSessionItems}
							inReviewMode={inReviewMode}
							currentItemIndex={currentItemIndex}
						/>
					</Box>
					<Box flex={1} justifyContent="flex-end" flexDirection="row">
						<StudySessionAppbarRight
							handleAction={handleClickRightCta}
							timeRemaining={timeRemaining}
							showPause={showPause}
							isTimerExpired={isTimerExpired}
							inReviewMode={inReviewMode}
							skillName={skillName}
						/>
					</Box>
				</Box>
			</Appbar.Header>
			{showToolbar && (allowMarking || !inReviewMode) && (
				<Surface
					style={{
						backgroundColor: theme.colors.surface,
						elevation: 0,
						borderBottomWidth: 1,
						borderBottomColor: theme.palette.divider,
					}}
				>
					<Box justifyContent="space-between" flexDirection="row" padding={16}>
						{inReviewMode ? (
							<Box />
						) : (
							<Box flexDirection="row" gap={8} alignItems="center">
								{showCalculator && (
									<TooltipWrapper title={tooltips?.calculator ?? ""}>
										<Button
											icon="calculator"
											textColor={theme.colors.onSurface}
											onPress={() => handleClickToolbarActions("calculator")}
										>
											Calculator
										</Button>
									</TooltipWrapper>
								)}
								<TooltipWrapper title={tooltips?.highlight ?? ""}>
									<Button
										textColor={theme.colors.onSurface}
										icon="marker"
										onPress={() => handleClickToolbarActions("highlight")}
									>
										Highlight
									</Button>
								</TooltipWrapper>
							</Box>
						)}
						{allowMarking ? (
							<MarkToolbarButton
								isMarked={isMarked}
								tooltips={tooltips}
								onPress={() => handleClickToolbarActions("mark")}
							/>
						) : (
							<Box />
						)}
					</Box>
				</Surface>
			)}
		</Box>
	);
};

export default SummativeSessionAppbar;
